/**
 * Permission identifiers as defined and enforced by the backend.
 *
 * These MUST mirror `Permission` in
 * backend/apps/auth_api/authorization/permission.py exactly. The whoami endpoint
 * returns the user's effective permissions using these string values (see
 * `UserPermissions` in Types), and the frontend authorizes UI by checking them
 * via `useAllowedTo` / `<AllowedTo>`. This is the single source of truth for what
 * a user can do — there is intentionally no role -> permission mapping on the
 * frontend.
 */
export const PERMISSION = {
  CREATE_ORGANIZATION: "create_organization",
  CREATE_GROUP: "create_group",
  DELETE_GROUP: "delete_group",
  VIEW_GROUP: "view_group",
  CREATE_USER: "create_user",
  DELETE_USER: "delete_user",
  EDIT_MEMBER_INFO: "edit_member_info",
  TRIGGER_PASSWORD_RESET: "trigger_password_reset",
  INVITE_MEMBERS_TO_ORGANIZATION: "invite_members_to_organization",
  DEACTIVATE_MEMBER_ACCOUNT: "deactivate_member_account",
  ADD_ORGANIZATION_MEMBERS_TO_GROUP: "add_organization_members_to_group",
  REMOVE_MEMBER_FROM_GROUP: "remove_member_from_group",
  SET_GROUPS_FOR_USER: "set_groups_for_user",
  MODIFY_MEMBERS_GROUP_ROLE: "modify_members_group_role",
  MODIFY_MEMBERS_ORGANIZATION_ROLE: "modify_members_organization_role",
  SET_GROUP_AS_DEFAULT: "set_group_as_default",
  VIEW_ALL_GROUPS: "view_all_groups",
  VIEW_ALL_ORGANIZATION_MEMBERS: "view_all_organization_members",
  VIEW_GROUP_MEMBERS: "view_group_members",
  UPLOAD_FILE: "upload_file",
  REPROCESS_FILE: "reprocess_file",
  DELETE_FILE: "delete_file",
  EDIT_FILE_SOFTWARE_INFO: "edit_file_software_info",
  VIEW_ALL_FILESTREAM_FILES: "view_all_filestream_files",
  MODIFY_FILE_APPROVAL: "modify_file_approval_status",
  DIFF_FILES: "diff_files",
  EXPORT_SARIF_REPORT: "export_sarif_report",
  EXPORT_CYCLONEDX_REPORT: "export_cyclonedx_report",
  EXPORT_SPDX_REPORT: "export_spdx_report",
  EXPORT_RL_JSON_REPORT: "export_rl_json_report",
  EXPORT_RL_CHECKS_REPORT: "export_rl_checks_report",
  EXPORT_RL_CVE_REPORT: "export_rl_cve_report",
  EXPORT_RL_URI_REPORT: "export_rl_uri_report",
  EXPORT_RL_DIFF_REPORT: "export_rl_diff_report",
  EXPORT_RL_SAFE_ARCHIVE: "export_rl_safe_report",
  EXPORT_RL_SUMMARY_PDF: "export_rl_summary_pdf",
  CREATE_PROJECT: "create_project",
  DELETE_PROJECT: "delete_project",
  CREATE_PACKAGE: "create_package",
  DELETE_PACKAGE: "delete_package",
  REANALYZE_PACKAGE: "reanalyze_package",
  UPLOAD_VERSION: "upload_version",
  REANALYZE_VERSION: "reanalyze_version",
  DELETE_VERSION: "delete_version",
  EDIT_VERSION_SOFTWARE_INFO: "edit_version_software_info",
  MODIFY_VERSION_APPROVAL: "modify_version_approval_status",
  VIEW_PROJECTS: "view_projects",
  VIEW_PACKAGES: "view_packages",
  VIEW_VERSIONS: "view_versions",
  CONFIGURE_ORGANIZATION_POLICY_PROFILE:
    "configure_organization_policy_profile",
  VIEW_ORGANIZATION_POLICY_PROFILE: "view_organization_policy_profile",
  CONFIGURE_GROUP_POLICY_PROFILE: "configure_group_policy_profile",
  VIEW_GROUP_POLICY_PROFILE: "view_group_policy_profile",
  ASSIGN_MONTHLY_PROCESSING_CAPACITY_TO_ORGANIZATION:
    "assign_monthly_processing_capacity_to_organization",
  VIEW_ORGANIZATION_MONTHLY_CAPACITY_AND_USAGE:
    "view_organization_monthly_capacity_and_usage",
  ADD_GROUP_CAPACITY_LIMIT: "add_group_capacity_limit",
  VIEW_MONTHLY_USAGE_PER_GROUP: "view_monthly_usage_per_group",
  VIEW_CLI_SITE_KEY_ALLOCATIONS: "view_cli_site_key_allocations",
  MODIFY_CLI_SITE_KEY_ALLOCATIONS: "modify_cli_site_key_allocations",
  VIEW_GROUP_USAGE_DETAILS: "view_group_usage_details",
  SETUP_SSO: "setup_sso",
  MODIFY_SESSION_CONFIGURATION: "modify_session_configuration",
  MODIFY_AI_CONFIGURATION: "modify_ai_configuration",
  GENERATE_PERSONAL_ACCESS_TOKEN: "generate_personal_access_token",
  REVOKE_PERSONAL_ACCESS_TOKEN: "revoke_personal_access_token",
  LIST_USER_PERSONAL_ACCESS_TOKENS: "list_user_personal_access_tokens",
  REVOKE_USER_PERSONAL_ACCESS_TOKENS: "revoke_user_personal_access_token",
  REVOKE_ALL_PERSONAL_ACCESS_TOKENS: "revoke_all_personal_access_tokens",
  DOWNLOAD_FILESTREAM_FILES: "download_filestream_files",
  DOWNLOAD_VERSIONS: "download_versions",
  GET_REPORT_FOR_PURL: "get_report_for_purl",
  SHARE_REPORT: "share_report",
  LIST_SHARED_REPORTS: "list_shared_reports",
  REALLOCATE_QUOTAS: "reallocate_quotas",
  GENERATE_INSIGHTS_SEARCH: "generate_insights_search",
  LIST_INSIGHTS_SEARCH: "list_insights_searches",
  VIEW_AUDIT: "view_audit",
  BROWSE_SOFTWARE_CATALOG: "browse_software_catalog",
  BROWSE_EXTERNAL_SOFTWARE_CATALOG: "browse_external_software_catalog",
} as const;

export type Permission = (typeof PERMISSION)[keyof typeof PERMISSION];
